import React from 'react'
import { Tooltip, Icon, useResponsiveDropdown } from '@sketch/components'
import { ReactComponent as PersonsIcon } from '@sketch/icons/persons-2-24'
import { IsSignedIn, useFlag } from '@sketch/modules-common'
import { useShareModal } from './PageCanvasViewHeader.hooks'
import { ActionsNavbarItem, IconButton } from './PageCanvasViewHeader.styles'
import { useVersioning } from 'modules/versioning'
import { ReactComponent as BellIcon } from '@sketch/icons/bell-24'
import { ReactComponent as BellStrikeIcon } from '@sketch/icons/bell-strikethrough-24'
import NotificationStatusDropdown from 'modules/annotations/components/NotificationPartialStatusDropdown'
import useDocumentNotificationSubscription from 'modules/annotations/operations/useNotificationSubscription'
import {
  tooltipCopy,
  DocumentToggleProps,
} from 'modules/shares/components/DocumentNotificationButton/DocumentNotificationButton'
// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { NotificationSubscriptionStatus } from '@sketch/gql-types/expansive'

const icons: { [key in NotificationSubscriptionStatus]: React.ElementType } = {
  ON: BellIcon,
  OFF: BellStrikeIcon,
  PARTIAL: BellIcon,
} as const

const DocumentNotificationButtonNavbar: React.FC<DocumentToggleProps> = ({
  currentStatus,
  identifier,
}) => {
  const updateNotificationSubscriptions = useDocumentNotificationSubscription()
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: NotificationStatusDropdown,
    dropdownProps: {
      type: 'share',
      notificationStatus: currentStatus,
      onNotificationChange: state =>
        updateNotificationSubscriptions({
          shareIdentifier: identifier,
          state,
        }),
    },
  })

  const getButton = () => {
    if (currentStatus === 'PARTIAL') {
      return (
        <>
          <IconButton
            active
            data-testid="document-notification-status"
            size="32"
            {...buttonProps}
          >
            <Icon as={icons[currentStatus]} aria-label="Follow document" />
          </IconButton>
          {content}
        </>
      )
    }

    const newStatus = currentStatus === 'ON' ? 'OFF' : 'ON'

    return (
      <IconButton
        active={currentStatus === 'ON'}
        data-testid="document-notification-status"
        size="32"
        onClick={() => {
          updateNotificationSubscriptions({
            shareIdentifier: identifier,
            state: newStatus,
          })
        }}
      >
        <Icon
          as={icons[currentStatus]}
          aria-label={
            currentStatus === 'ON' ? 'Unollow document' : 'Follow document'
          }
        />
      </IconButton>
    )
  }

  return (
    <Tooltip
      disableWhenTouchDevice
      placement="bottom"
      content={tooltipCopy[currentStatus]}
    >
      {getButton()}
    </Tooltip>
  )
}

export const DocumentActions = () => {
  const isNewSharingFFEnabled = useFlag('new-sharing')
  const showShareModal = useShareModal()
  const { share } = useVersioning()

  if (!isNewSharingFFEnabled || !share.userCanUpdateSettings) {
    return null
  }

  return (
    <IsSignedIn>
      <Tooltip
        content={<>Share document&hellip;</>}
        disableWhenTouchDevice
        placement="bottom"
      >
        <ActionsNavbarItem>
          <IconButton onClick={showShareModal}>
            <Icon as={PersonsIcon} aria-label="Share document" />
          </IconButton>
        </ActionsNavbarItem>
      </Tooltip>
      <DocumentNotificationButtonNavbar
        identifier={share.identifier}
        currentStatus={share.subscriptionStatus}
      />
    </IsSignedIn>
  )
}

export default DocumentActions
